import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { useAppSelector } from '@/store/hooks';
import { hasPermission } from '@/lib/auth/permissions';
import { deleteRole, getRoles, roleQueryKeys } from '@/features/roles/api';
import type { Role } from '@/features/roles/role';
import { ApiError } from '@/lib/api';

import { ListPageShell } from '@/components/layout/ListPageShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, RefreshCw, Trash2, Pencil } from '@/lib/icons';
import { ButtonSpinner, SectionLoading } from '@/components/feedback';

export const Route = createFileRoute('/_layout/roles/')({
  component: RolesListPage,
});

function RolesListPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUser = useAppSelector((state) => state.auth.user);
  const canManage = hasPermission(currentUser, 'users', 'update');
  const canCreate = hasPermission(currentUser, 'users', 'create');
  const canDelete = hasPermission(currentUser, 'users', 'delete');

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: roleQueryKeys.lists(),
    queryFn: getRoles,
  });

  const roles = data?.value ?? [];

  const deleteDialog = useConfirmDialog({
    title: t('roles.delete.title'),
    description: t('roles.delete.description'),
    confirmText: t('common.actions.delete'),
    cancelText: t('common.actions.cancel'),
    variant: 'destructive',
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: (result, id) => {
      setPendingDeleteId(null);
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: roleQueryKeys.lists() });
        toast({
          title: t('common.success'),
          description: t('roles.delete.success'),
        });
      } else {
        const inUse = (result.statusCode ?? 0) === 409;
        toast({
          title: t('common.error'),
          description: inUse
            ? t('roles.delete.inUse')
            : (result.errors?.[0] ?? t('common.error')),
          variant: 'destructive',
        });
      }
      void id;
    },
    onError: (error: unknown) => {
      setPendingDeleteId(null);
      const inUse = error instanceof ApiError && error.statusCode === 409;
      toast({
        title: t('common.error'),
        description: inUse
          ? t('roles.delete.inUse')
          : error instanceof Error
            ? error.message
            : t('common.error'),
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (role: Role) => {
    const confirmed = await deleteDialog.confirm();
    if (!confirmed) return;
    setPendingDeleteId(role.id);
    deleteMutation.mutate(role.id);
  };

  if (!canManage) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
        <p className="text-muted-foreground">
          {t('common.noPermissionToView')}
        </p>
      </div>
    );
  }

  return (
    <ListPageShell
      title={t('roles.list.title')}
      actions={
        <>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            title={t('common.refreshList')}
          >
            {isFetching ? (
              <ButtonSpinner />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          {canCreate && (
            <Button asChild>
              <Link to="/roles/new">
                <Plus className="h-4 w-4 mr-1" />
                {t('roles.list.add')}
              </Link>
            </Button>
          )}
        </>
      }
      hasData={!isLoading && roles.length > 0}
      emptyState={
        isLoading ? (
          <SectionLoading />
        ) : (
          <div className="text-center text-muted-foreground py-12">
            {t('roles.list.empty')}
          </div>
        )
      }
    >
      <div className="rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('common.description')}</TableHead>
              <TableHead>{t('roles.list.flags')}</TableHead>
              <TableHead className="text-right">
                {t('common.actionsTitle')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell className="font-medium">{role.name}</TableCell>
                <TableCell className="max-w-md text-muted-foreground">
                  {role.description}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {role.isSystem && (
                      <Badge variant="secondary">
                        {t('roles.flags.system')}
                      </Badge>
                    )}
                    {role.isGeneric && (
                      <Badge variant="outline">
                        {t('roles.flags.generic')}
                      </Badge>
                    )}
                    {role.bypassPermissions && (
                      <Badge variant="default">{t('roles.flags.bypass')}</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/roles/$roleId"
                      params={{ roleId: role.id }}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      {t('common.actions.edit')}
                    </Link>
                  </Button>
                  {canDelete && !role.isSystem && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      disabled={
                        pendingDeleteId === role.id &&
                        deleteMutation.isPending
                      }
                      onClick={() => handleDelete(role)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('common.actions.delete')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <deleteDialog.ConfirmDialog />
    </ListPageShell>
  );
}
